import React, { useState } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import { useAuth } from '../../context/AuthContext';
    import SafeIcon from '../../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiPlus } = FiIcons;

    const ProfileFormModal = ({ profile, onClose, onSuccess }) => {
        const { user } = useAuth();
        const [formData, setFormData] = useState({
            name: profile?.name || '',
            instagram_handle: profile?.instagram_handle || '',
            bio: profile?.bio || '',
            tags: profile?.tags || [],
        });
        const [newTag, setNewTag] = useState('');
        const [saving, setSaving] = useState(false);
        const [error, setError] = useState('');

        const handleChange = (e) => {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        };

        const handleAddTag = (e) => {
            e.preventDefault();
            if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
                setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
                setNewTag('');
            }
        };

        const handleRemoveTag = (tagToRemove) => {
            setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!user) { setError('You must be logged in to submit a profile.'); return; }
            
            setSaving(true);
            setError('');

            try {
                const profileData = {
                    ...formData,
                    instagram_handle: formData.instagram_handle.replace('@', ''),
                    user_id: user.id,
                    status: 'pending', // Always set to pending on create/update
                };

                let response;
                if (profile?.id) {
                    // Update existing profile
                    response = await supabase.from('profiles').update(profileData).eq('id', profile.id);
                } else {
                    // Insert new profile
                    response = await supabase.from('profiles').insert([profileData]);
                }
                
                if (response.error) throw response.error;
                onSuccess();
                onClose();
            } catch (error) {
                console.error('Error saving profile:', error);
                setError(error.message || 'Error saving profile');
            } finally {
                setSaving(false);
            }
        };

        return (
            <AnimatePresence>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">{profile ? 'Edit' : 'Submit'} Profile</h2>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <SafeIcon icon={FiX} className="text-xl" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                    {error}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram Handle</label>
                                <input type="text" name="instagram_handle" value={formData.instagram_handle} onChange={handleChange} placeholder="username (without @)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bio / Description</label>
                                <textarea name="bio" value={formData.bio} onChange={handleChange} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500 resize-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                                {formData.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {formData.tags.map((tag, index) => (
                                            <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                                                {tag}
                                                <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 text-yellow-600 hover:text-yellow-700">
                                                    <SafeIcon icon={FiX} className="text-xs" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex space-x-2">
                                    <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add up to 5 tags" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500" />
                                    <button
                                        type="button"
                                        onClick={handleAddTag}
                                        disabled={formData.tags.length >= 5}
                                        className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                                    >
                                        <SafeIcon icon={FiPlus} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-4 pt-4">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving} className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Saving...' : 'Submit for Review'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    };

    export default ProfileFormModal;